import { DefaultError, useMutation } from '@tanstack/react-query';
import { useQueryToast } from 'hooks';
import i18next from 'i18next';
import { apiClient } from 'utils';
import { useResponseHandler } from 'utils/service/responseHandler';
import { IMusicProductDigitalImageResponse, IMusicProductDigitalResponse } from './types/query';

export const usePostMusicProductDigitalImageThumbnail = () => {
  const { responseHandler } = useResponseHandler();
  const { loading, dismiss, success } = useQueryToast('usePostMusicProductDigitalImageThumbnail');
  return useMutation<
    IMusicProductDigitalImageResponse,
    DefaultError,
    { file: File; id: string; version: number }
  >({
    mutationFn: async ({ file, id, version }) => {
      loading(
        i18next.t('service-message.add.loading', {
          what: i18next.t('music-product-digital.album-cover-what'),
        }),
      );
      const formData = new FormData();

      formData.append('file', file);

      const response = await apiClient<FormData>({
        method: 'POST',
        url: `MusicProductDigital/Thumbnail`,
        body: formData,
        params: { mpdid: id, version },
        withContentType: false,
      });

      return responseHandler(response, {
        groupName: 'usePostMusicProductDigitalImageThumbnail',
        notFoundPath: '/music-product-digital',
      });
    },
    onSuccess: () => {
      success(
        i18next.t('service-message.add.success', {
          what: i18next.t('music-product-digital.album-cover-what'),
        }),
      );
    },
    onError: dismiss,
  });
};

export const usePutMusicProductDigitalImageThumbnail = () => {
  const { responseHandler } = useResponseHandler();
  const { loading, dismiss, success } = useQueryToast('usePutMusicProductDigitalImageThumbnail');
  return useMutation<
    IMusicProductDigitalImageResponse,
    DefaultError,
    { file: File; id: string; version: number }
  >({
    mutationFn: async (body) => {
      loading(
        i18next.t('service-message.change.loading', {
          what: i18next.t('music-product-digital.album-cover-what'),
        }),
      );

      const formData = new FormData();

      formData.append('file', body.file);

      const response = await apiClient<FormData>({
        method: 'PUT',
        url: `MusicProductDigital/Thumbnail`,
        body: formData,
        params: { mpdid: body.id, version: body.version },
        withContentType: false,
      });

      return responseHandler(response, {
        groupName: 'usePutMusicProductDigitalImageThumbnail',
        notFoundPath: '/music-product-digital',
      });
    },
    onSuccess: () => {
      success(
        i18next.t('service-message.change.success', {
          what: i18next.t('music-product-digital.album-cover-what'),
        }),
      );
    },
    onError: dismiss,
  });
};

export const useDeleteMusicProductDigitalImageThumbnail = (id: string) => {
  const { responseHandler } = useResponseHandler();
  const { loading, dismiss, success } = useQueryToast('useDeleteMusicProductDigitalImageThumbnail');
  return useMutation<IMusicProductDigitalResponse, DefaultError>({
    mutationFn: async () => {
      loading(
        i18next.t('service-message.remove.loading', {
          what: i18next.t('music-product-digital.album-cover-what'),
        }),
      );

      const response = await apiClient<FormData>({
        method: 'DELETE',
        url: `MusicProductDigital/${id}/Thumbnail`,
      });

      return responseHandler(response, {
        groupName: 'useDeleteMusicProductDigitalImageThumbnail',
        notFoundPath: '/music-product-digital',
      });
    },
    onSuccess: () => {
      success(
        i18next.t('service-message.remove.success', {
          what: i18next.t('music-product-digital.album-cover-what'),
        }),
      );
    },
    onError: dismiss,
  });
};
